import React, { useState, useEffect, useRef } from 'react';
import { Send } from 'lucide-react';

function ChatInterface({ sessionId, messages, onSendMessage, loading, userName = "User" }) {
  const [message, setMessage] = useState('');
  const [showOptions, setShowOptions] = useState(false);
  const [optionList, setOptionList] = useState([]);
  const [allowMultiple, setAllowMultiple] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [customOption, setCustomOption] = useState('');
  const messageEndRef = useRef(null);
  const optionsRef = useRef(null);
  
  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messageEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Extract options from the last bot message
  useEffect(() => {
    if (messages.length === 0) return;
    
    // Only show options after a bot message and not during loading
    if (messages[messages.length - 1].type === 'bot' && !loading) {
        const lastBotMessage = messages[messages.length - 1];
        
        if (lastBotMessage.options && lastBotMessage.options.length > 0) {
            setOptionList(lastBotMessage.options);
            setAllowMultiple(lastBotMessage.allowMultipleSelections || false);
            setShowOptions(true);
            setSelectedOptions([]);
            
            // Check if the message mentions multiple selection
            const messageText = lastBotMessage.content.toLowerCase();
            if (messageText.includes('select multiple') || 
                messageText.includes('choose multiple') || 
                messageText.includes('multiple options') ||
                messageText.includes('can select more than one') ||
                messageText.includes('feel free to select more')) {
                setAllowMultiple(true);
            }
        } else {
            setShowOptions(false);
        }
    } else {
        setShowOptions(false);
    }
  }, [messages, loading]);
  
  // Add focus to the options container when options appear
  useEffect(() => {
    if (showOptions && optionsRef.current) {
      optionsRef.current.focus();
    }
  }, [showOptions]);

  // Handle sending a text message
  const handleSend = () => {
    if (message.trim()) {
      onSendMessage(message);
      setMessage('');
      setShowOptions(false);
      setSelectedOptions([]);
      setCustomOption('');
    }
  };

  // Handle individual option clicks
  const handleOptionClick = (option) => {
    if (allowMultiple) {
      // For multiple selection - toggle the option
      setSelectedOptions(prev => 
        prev.includes(option) 
          ? prev.filter(item => item !== option) 
          : [...prev, option]
      );
    } else {
      // For single selection, immediately send
      onSendMessage(option);
      setShowOptions(false);
      setSelectedOptions([]);
      setCustomOption('');
    }
  };
  
  // Handle checkbox changes for multi-select
  const handleOptionToggle = (option) => {
    setSelectedOptions(prev => 
      prev.includes(option) 
        ? prev.filter(item => item !== option) 
        : [...prev, option]
    );
  };
  
  // Handle submitting multiple selected options
  const handleSubmitOptions = () => {
    let finalSelection = [...selectedOptions];
    
    if (customOption.trim()) {
      finalSelection.push(customOption.trim());
    }
    
    if (finalSelection.length > 0) {
      onSendMessage(finalSelection.join(', '));
      setShowOptions(false);
      setSelectedOptions([]);
      setCustomOption('');
    }
  };

  // Handle Enter key press in the input field
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      if (showOptions && allowMultiple && (selectedOptions.length > 0 || customOption.trim())) {
        handleSubmitOptions();
      } else if (!showOptions && message.trim()) {
        handleSend();
      }
    }
  };

  return (
    <div className="chat-interface">
      <div className="chat-messages">
        {messages.map((msg, index) => (
          <div key={index} className={`message ${msg.type}`}>
            {msg.type === 'bot' && (
              <div className="avatar bot-avatar">
                <span className="avatar-text">L</span>
              </div>
            )}
            <div className={`message-content ${msg.type}-content`}>
              {msg.content}
            </div>
            {msg.type === 'user' && (
              <div className="avatar user-avatar">
                <span className="avatar-text">{userName.charAt(0)}</span>
              </div>
            )}
          </div>
        ))}
        {loading && (
          <div className="message bot">
            <div className="avatar bot-avatar">
              <span className="avatar-text">L</span>
            </div>
            <div className="message-content bot-content loading">
              Typing
            </div>
          </div>
        )}
        <div ref={messageEndRef} />
      </div>
      
      {showOptions && optionList.length > 0 && (
        <div className="options-wrapper">
          <div className="option-selector">
            {allowMultiple && (
              <div className="multi-select-hint">
                You can select multiple options that apply to you
              </div>
            )}
            <div 
              className="options-container" 
              ref={optionsRef}
              tabIndex={0}
            >
              {optionList.map((option, index) => (
                allowMultiple ? (
                  <div key={index} className="option-item">
                    <input
                      type="checkbox"
                      id={`option-${index}`}
                      checked={selectedOptions.includes(option)}
                      onChange={() => handleOptionToggle(option)}
                    />
                    <label htmlFor={`option-${index}`}>{option}</label>
                  </div>
                ) : (
                  <button
                    key={index}
                    className={`option-button ${selectedOptions.includes(option) ? 'selected' : ''}`}
                    onClick={() => handleOptionClick(option)}
                  >
                    {option}
                  </button>
                )
              ))}
              <div className="custom-option-input-container">
                <span className="custom-option-label">Other (please specify):</span>
                <input
                  type="text"
                  placeholder="Type your own response..."
                  value={customOption}
                  onChange={(e) => setCustomOption(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="custom-option-input"
                />
              </div>
            </div>
            
            {allowMultiple && (
              <button 
                className="submit-options"
                onClick={handleSubmitOptions}
                disabled={selectedOptions.length === 0 && !customOption.trim()}
              >
                Submit {selectedOptions.length > 0 ? `(${selectedOptions.length} selected)` : ""}
              </button>
            )}
          </div>
        </div>
      )}
      
      <div className="message-input">
        <input
          type="text"
          placeholder="Type your message..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={loading}
        />
        <button 
          className="send-button"
          onClick={handleSend}
          disabled={loading || !message.trim()}
        >
          <Send size={20} />
        </button>
      </div>
    </div>
  );
}

export default ChatInterface;